import AddLocationIcon from '@mui/icons-material/AddLocation'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import LoadingButton from '@mui/lab/LoadingButton'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import * as turf from '@turf/turf'
import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { v4 as UUID } from 'uuid'
import AppContext from '../../context/appContext'
import { queryKeys } from '../../hooks/data/queryKeys'
import { useUserRole } from '../../hooks/useUserRole'
import { ruutsApi } from '../../services/ruutsApi'
import { getMonitoringSiteAssociations } from '../../services/farmMapping/monitoring/getMonitoringSiteAssociations'
import { useAddMonitoringSite, useDeleteMonitoringSite } from '../../hooks/data/useMonitoringSites'
import { useCustomSnackbarError } from '../../utils/Snackbar/useCustomSnackbarError'
import { siteConflictMessage, withDisplayMessage } from '../../utils/Errors/siteConflictMessage'
import { isEligibilityEnabled } from '../../utils/programConfig/areaSubtractions'
import {
  computeStrataWithRandomSites,
  computeLockedStrataNames,
  partitionStrataForRandomization,
} from '../../utils/samplePlanning/strataGating'
import { getManualSiteByDisplayIndex } from '../../utils/samplePlanning/getManualSiteByDisplayIndex'
import { isUnsavedSite } from '../../utils/samplePlanning/isUnsavedSite'
import { randomizerVersions } from '../../utils/constants'
import {
  getInitialSiteIndex,
  getInitialSiteIndexFromSavedSites,
} from '../../utils/randomize/getInitialSiteIndex'
import randomPointsInPolygonV2 from '../../utils/randomize/randomizePointsV2'
import randomPointsInPolygonV3 from '../../utils/randomize/randomizePointsV3'
import randomPointsInPolygonV4 from '../../utils/randomize/randomizePointsV4'
import CustomAlert from '../Alert/CustomAlert'
import AddManualSiteFormModal from './AddManualSiteFormModal'
import { buildManualSiteStrata, findManualSiteStratum } from './manualSiteStratum'
import ReRandomizeModal from './ReRandomizeModal'
import { StrataAccordion } from './StrataAccordion'

// Reference identity cannot survive a parent state rebuild across the save's await, so sites are
// written back by a stable key. Names are deliberately not part of it: V2/V3 derive the saved
// name at save time, so two sites in one stratum can share one and collapse into a single entry.
const siteMatchKey = site => site.properties?.clientId ?? site.properties?.id ?? null

// The one chokepoint that guarantees the key exists: randomizer output reaches the panel state
// only through setSitesWithClientIds below, and manual sites stamp their own on creation.
const stampClientIds = sitesByStrata =>
  Object.fromEntries(
    Object.entries(sitesByStrata ?? {}).map(([strataName, sites]) => [
      strataName,
      (sites ?? []).map(site =>
        site.properties?.clientId
          ? site
          : { ...site, properties: { ...site.properties, clientId: UUID() } },
      ),
    ]),
  )

const SamplePlanningPanel = ({
  farm,
  stratas,
  wasSaved,
  setWasSaved,
  eligibilityAreas,
  monitoringSitesBySamplingAreas,
  setMonitoringSitesBySamplingAreas,
  randomizeVersion,
  mapClickCoordinates,
  uncroppedStratas,
  projectAreaGeometry,
  effectiveAreasData,
}) => {
  const { handleError } = useCustomSnackbarError()
  const [loading, setLoading] = useState(false)
  const [strataSites, setStrataSites] = useState({})
  const [openReRandomizeModal, setOpenReRandomizeModal] = useState(false)
  const [reRandomizeIndex, setReRandomizeIndex] = useState()
  const [reRandomizeStrata, setReRandomizeStrata] = useState()
  const [openManualSiteForm, setOpenManualSiteForm] = useState(false)
  const [selectedStrataForManualSite, setSelectedStrataForManualSite] = useState(null)
  const [initialCoordinates, setInitialCoordinates] = useState([null, null])
  const { isViewOnly } = useUserRole()
  const [expandedAccordion, setExpandedAccordion] = useState(null)
  const {
    partialChanges,
    programConfig,
    setPartialChanges,
    setConfirmationModalConfig,
    setLoadingModalConfig,
  } = useContext(AppContext)

  const reRandomize = programConfig?.reRandomizeEnabled ?? false
  const { mutateAsync: deleteSite } = useDeleteMonitoringSite({ farmId: farm?.id })
  const queryClient = useQueryClient()
  // skipInvalidation: one invalidation after the batch settles instead of one per site, so the
  // parent cannot rebuild the sites out from under the id write-back below.
  const { mutateAsync: addSite } = useAddMonitoringSite({
    farmId: farm?.id,
    skipInvalidation: true,
    suppressGlobalError: true,
  })

  // saveSamplePlanning captures the prop before its awaits; this mirrors the latest render so the
  // write-back decides and matches against one and the same state.
  const monitoringSitesRef = useRef(monitoringSitesBySamplingAreas)
  monitoringSitesRef.current = monitoringSitesBySamplingAreas

  const setSitesWithClientIds = useCallback(
    updater =>
      setMonitoringSitesBySamplingAreas(prev =>
        stampClientIds(typeof updater === 'function' ? updater(prev) : updater),
      ),
    [setMonitoringSitesBySamplingAreas],
  )

  const randomizeAlgorithm = useMemo(() => {
    switch (randomizeVersion) {
      case randomizerVersions.V2:
        return randomPointsInPolygonV2
      case randomizerVersions.V3:
        return randomPointsInPolygonV3
      case randomizerVersions.V4:
        return randomPointsInPolygonV4
      default:
        return undefined
    }
  }, [randomizeVersion])

  const disabledStrataNames = useMemo(() => {
    const effectiveAreas = effectiveAreasData?.effectiveAreas
    if (!effectiveAreas) return new Set()
    return new Set(effectiveAreas.filter(ea => ea.effectiveArea === 0).map(ea => ea.name))
  }, [effectiveAreasData])

  const manualSiteStrata = useMemo(
    () => buildManualSiteStrata(stratas, uncroppedStratas),
    [stratas, uncroppedStratas],
  )

  const strataWithRandomSites = useMemo(
    () => computeStrataWithRandomSites(monitoringSitesBySamplingAreas || {}),
    [monitoringSitesBySamplingAreas],
  )

  const lockedStrataNames = useMemo(
    () => computeLockedStrataNames(disabledStrataNames, strataWithRandomSites),
    [disabledStrataNames, strataWithRandomSites],
  )

  const checkUnsavedSites = useCallback(() => {
    if (!monitoringSitesBySamplingAreas) {
      setPartialChanges(false)
      return
    }
    const pendingChanges = Object.values(monitoringSitesBySamplingAreas).some(sites =>
      sites?.some(site => !site.properties?.id || site.properties?.newManualSite),
    )
    setPartialChanges(pendingChanges)
  }, [monitoringSitesBySamplingAreas, setPartialChanges])

  useEffect(() => {
    checkUnsavedSites()
  }, [checkUnsavedSites])

  const hasUnsavedSites = useCallback(
    strataName => {
      if (!monitoringSitesBySamplingAreas || !monitoringSitesBySamplingAreas[strataName]) {
        return false
      }
      return monitoringSitesBySamplingAreas[strataName].some(
        site => !site.properties?.id || site.properties?.newManualSite,
      )
    },
    [monitoringSitesBySamplingAreas],
  )

  const randomizeMonitoringSites = async () => {
    if (!randomizeAlgorithm) return

    setLoading(true)

    const randomMonitoringSites = {}
    const { toRandomize } = partitionStrataForRandomization(stratas.features, lockedStrataNames)

    try {
      if (randomizeVersion === randomizerVersions.V4) {
        await Promise.all(
          toRandomize.map(async strata => {
            // Calcular índice inicial basado en sitios existentes
            const existingSites = monitoringSitesBySamplingAreas[strata.properties.name] || []
            const initialIndex = getInitialSiteIndexFromSavedSites(
              strata.properties.name,
              existingSites,
            )

            const generateResponse = await randomizeAlgorithm({
              samples: strataSites[strata.properties.name],
              strata,
              samplingAreaId: strata.properties.id,
              initialIndex,
            })

            randomMonitoringSites[strata.properties.name] = generateResponse
          }),
        )
      } else {
        const eligibilityAreasToSubtract = isEligibilityEnabled(programConfig)
          ? eligibilityAreas || []
          : []
        toRandomize.forEach(strata => {
          // Calcular índice inicial basado en sitios existentes
          const existingSites = monitoringSitesBySamplingAreas[strata.properties.name] || []
          const initialIndex = getInitialSiteIndexFromSavedSites(
            strata.properties.name,
            existingSites,
          )

          randomMonitoringSites[strata.properties.name] = randomizeAlgorithm(
            strataSites[strata.properties.name],
            strata,
            [],
            undefined,
            eligibilityAreasToSubtract,
            projectAreaGeometry,
            initialIndex,
          )
        })
      }

      if (randomizeVersion !== randomizerVersions.V4) {
        Object.keys(randomMonitoringSites).forEach(strata => {
          randomMonitoringSites[strata] = randomMonitoringSites[strata].map(site => ({
            ...site,
            properties: {
              ...site.properties,
              randomizeCounter: site.properties.randomizeCounter || 0,
              randomizeReason: [],
              isRandomSite: true,
            },
          }))
        })
      }

      setSitesWithClientIds(prev => {
        // Recompute the lock set from the freshest state (prev) rather than the snapshot captured
        // before the await: a strata whose random sites were persisted while randomization was in
        // flight must stay locked here, otherwise its saved sites would be overwritten.
        const freshLockedStrataNames = computeLockedStrataNames(
          disabledStrataNames,
          computeStrataWithRandomSites(prev || {}),
        )
        const newMonitoringSites = {}

        stratas.features.forEach(strata => {
          const strataName = strata.properties.name
          if (freshLockedStrataNames.has(strataName)) {
            newMonitoringSites[strataName] = prev[strataName]
            return
          }

          const randomSites = randomMonitoringSites[strataName] ?? []
          const placedSites = (prev[strataName] || []).filter(site => !site.properties.isRandomSite)
          newMonitoringSites[strataName] =
            placedSites.length > 0 ? [...placedSites, ...randomSites] : randomSites
        })

        return newMonitoringSites
      })
      setPartialChanges(true)
    } catch (error) {
      handleError(error)
    } finally {
      setLoading(false)
    }
  }

  const handleCloseReRandomizeModal = () => {
    setOpenReRandomizeModal(false)
  }

  const pendingStrataRef = useRef(null)

  const handleCloseWarning = useCallback(() => {
    setConfirmationModalConfig({ open: false })
    pendingStrataRef.current = null
  }, [setConfirmationModalConfig])

  const handleConfirmWarning = useCallback(() => {
    setSelectedStrataForManualSite(pendingStrataRef.current)
    setOpenManualSiteForm(true)
    handleCloseWarning()
  }, [handleCloseWarning])

  const reRandomizeMonitoringSite = async (strata, index, reason) => {
    if (strata && index !== undefined && randomizeAlgorithm) {
      const prepareSites = prev => {
        const placedSites = {}
        Object.keys(prev).forEach(strataName => {
          placedSites[strataName] = prev[strataName].filter(site => !site.properties.isRandomSite)
        })
        const randomSites = {}
        Object.keys(prev).forEach(strataName => {
          randomSites[strataName] = prev[strataName].filter(site => site.properties.isRandomSite)
        })
        const oldSite = { ...randomSites[strata.properties.name][index] }
        oldSite.properties = { ...oldSite.properties }
        oldSite.properties.randomizeReason = [...(oldSite.properties.randomizeReason ?? []), reason]
        randomSites[strata.properties.name] = randomSites[strata.properties.name].filter(
          (_, i) => i !== index,
        )
        const allExistingSites = [
          ...(placedSites[strata.properties.name] || []),
          ...(randomSites[strata.properties.name] || []),
        ]
        const initialIndex = getInitialSiteIndex(strata.properties.name, allExistingSites)
        return { placedSites, randomSites, oldSite, initialIndex }
      }

      if (randomizeVersion === randomizerVersions.V4) {
        try {
          const { oldSite } = prepareSites(monitoringSitesBySamplingAreas)
          const generated = await randomizeAlgorithm({
            samples: 1,
            strata,
            samplingAreaId: strata.properties.id,
            initialIndex: 1,
          })
          const newPoint = generated?.[0]
          if (!newPoint) {
            throw new Error('No se pudo generar un punto de reemplazo')
          }
          const replacementSite = {
            ...newPoint,
            properties: {
              ...oldSite.properties,
              seed: newPoint.properties.seed,
              randomizeCounter: (oldSite.properties.randomizeCounter ?? 0) + 1,
            },
          }
          setSitesWithClientIds(prev => {
            const { placedSites: freshPlaced, randomSites: freshRandom } = prepareSites(prev)
            const updatedRandom = [...freshRandom[strata.properties.name]]
            updatedRandom.splice(index, 0, replacementSite)
            return {
              ...prev,
              [strata.properties.name]: [...freshPlaced[strata.properties.name], ...updatedRandom],
            }
          })
          setPartialChanges(true)
        } catch (error) {
          handleError(error)
        }
      } else {
        setSitesWithClientIds(prev => {
          const { placedSites, randomSites, oldSite, initialIndex } = prepareSites(prev)
          const eligibilityAreasToSubtract = isEligibilityEnabled(programConfig)
            ? eligibilityAreas || []
            : []
          const newMonitoringSitesStrata = randomizeAlgorithm(
            strataSites[strata.properties.name],
            strata,
            randomSites[strata.properties.name],
            index,
            eligibilityAreasToSubtract,
            projectAreaGeometry,
            initialIndex,
          )
          // After sorting, the new point may not be at `index` anymore.
          // Find it by checking for the only site missing `isRandomSite`
          // (all existing random sites already have it set).
          const newSiteIndex = newMonitoringSitesStrata.findIndex(
            site => !site.properties.isRandomSite,
          )
          const targetIndex = newSiteIndex !== -1 ? newSiteIndex : index
          const updatedSite = {
            ...newMonitoringSitesStrata[targetIndex],
            properties: {
              ...oldSite.properties,
              randomizeCounter: (oldSite.properties.randomizeCounter ?? 0) + 1,
            },
          }
          newMonitoringSitesStrata[targetIndex] = updatedSite

          return {
            ...prev,
            [strata.properties.name]: [
              ...placedSites[strata.properties.name],
              ...newMonitoringSitesStrata,
            ],
          }
        })
        setPartialChanges(true)
      }
    }
  }

  const handleConfirmReRandomizeModal = (strata, index, reason) => {
    setOpenReRandomizeModal(false)
    reRandomizeMonitoringSite(strata, index, reason)
  }

  // Guarda todos los sitios creados, random y manuales.
  const saveSamplePlanning = async () => {
    const randomizerTypeId = await ruutsApi.getRandomizerTypeId(randomizeVersion)
    const isV4Randomizer = randomizeVersion === randomizerVersions.V4

    if (Object.keys(monitoringSitesBySamplingAreas).length > 0) {
      // Each entry keeps a reference back to the local site it came from, so a created id can be
      // written onto that exact site once the batch settles.
      const requests = []

      Object.entries(monitoringSitesBySamplingAreas).forEach(([strataName, sites]) => {
        if (sites.length > 0) {
          const newSites = sites.filter(isUnsavedSite)

          // Calcular índice inicial basado en sitios ya guardados (con id)
          const existingSites = sites.filter(site => !isUnsavedSite(site))
          const initialIndex = getInitialSiteIndex(strataName, existingSites)

          newSites.forEach((site, index) => {
            const samplingAreaId =
              site.properties.samplingAreaId ||
              stratas.features.find(s => s.properties.name === strataName)?.properties?.id ||
              null
            requests.push({
              strataName,
              site,
              matchKey: siteMatchKey(site),
              payload: {
                farmId: farm.id,
                name:
                  isV4Randomizer || !site.properties.isRandomSite
                    ? site.properties.name
                    : `${strataName}-${initialIndex + index}`,
                samplingAreaId: site.properties.samplingAreaId || samplingAreaId,
                plannedLocation: [site.geometry.coordinates[1], site.geometry.coordinates[0]],
                randomizeCounter: site.properties.randomizeCounter ?? 0,
                randomizeReason: site.properties.randomizeReason ?? [],
                isRandomSite: site.properties.isRandomSite || false,
                seed: site.properties.seed,
                randomizerTypeId: site.properties.isRandomSite ? randomizerTypeId : null,
              },
            })
          })
        }
      })

      // allSettled, not all: one rejection must not cost us the ids of the sites that did save,
      // or the next attempt would create them a second time.
      const results = await Promise.allSettled(
        requests.map(request => addSite({ site: request.payload })),
      )

      const created = []
      const failures = []
      results.forEach((result, index) => {
        const request = requests[index]
        // A fulfilled add with no id cannot be written back, so it is not saved as far as this
        // panel is concerned — marking it saved would strand it, uncreatable and uncorrectable.
        if (result.status === 'fulfilled' && result.value?.id) {
          created.push({ ...request, id: result.value.id })
          return
        }
        const error =
          result.reason ??
          new Error(
            `El servidor no devolvió el identificador del sitio "${request.site.properties.name}".`,
          )
        failures.push({ ...request, error })
      })

      // A created id nothing local can take is not a clean save: without this the site would be
      // dropped silently here and created a second time on the next attempt.
      const currentSites = monitoringSitesRef.current ?? {}
      const pendingIds = new Map(
        created.filter(entry => entry.matchKey).map(entry => [entry.matchKey, entry]),
      )

      const nextSites = {}
      Object.entries(currentSites).forEach(([strataName, sites]) => {
        nextSites[strataName] = (sites ?? []).map(candidate => {
          const key = siteMatchKey(candidate)
          const match = key ? pendingIds.get(key) : undefined
          if (!match) return candidate
          pendingIds.delete(key)
          // newManualSite must go with the id: every "is this saved?" check in this tree reads
          // both, so a manual site would keep testing as unsaved with only the id patched in.
          return {
            ...candidate,
            properties: { ...candidate.properties, id: match.id, newManualSite: false },
          }
        })
      })

      const unmatched = [...created.filter(entry => !entry.matchKey), ...pendingIds.values()]
      unmatched.forEach(entry =>
        failures.push({
          ...entry,
          error: new Error(
            `El sitio "${entry.site.properties.name}" se creó en el servidor pero no se pudo asociar al plan local.`,
          ),
        }),
      )

      if (created.length > unmatched.length) {
        setMonitoringSitesBySamplingAreas(nextSites)
      }

      if (failures.length === 1) {
        const [failure] = failures
        const conflictMessage = siteConflictMessage(failure)
        if (conflictMessage) {
          handleError(withDisplayMessage(failure.error, conflictMessage), conflictMessage, false)
        } else {
          handleError(failure.error)
        }
      } else if (failures.length > 1) {
        // One snackbar for the batch: a message per site buries the earlier ones.
        const details = failures.map(
          failure =>
            siteConflictMessage(failure) ??
            `No se pudo guardar el sitio "${failure.site.properties.name}".`,
        )
        const batchMessage = `No se pudieron guardar ${failures.length} sitios. ${details.join(' ')}`
        handleError(withDisplayMessage(failures[0].error, batchMessage), batchMessage, false)
      }

      if (failures.length === 0) {
        setWasSaved(true)
        // Only on a clean batch: a refetch while a write-back is still queued could race it and
        // hand back sites the panel has not stamped with their ids yet.
        queryClient.invalidateQueries({ queryKey: queryKeys.monitoringSites(farm?.id) })
        setPartialChanges(false)
      }
    }
  }

  const filteredStrataFeatures = useMemo(() => {
    if (!stratas) return []
    return stratas.features.filter(strata => strata.properties.belongToFarm)
  }, [stratas])

  useEffect(() => {
    setStrataSites(prev => {
      return filteredStrataFeatures.reduce((acc, strata) => {
        acc[strata.properties.name] = prev[strata.properties.name] ?? 0
        return acc
      }, {})
    })
  }, [filteredStrataFeatures])

  const checkValue = name => {
    if (!strataSites[name]) {
      return ''
    }
    if (strataSites[name] < 3 || strataSites[name] > 30) {
      return 'El número de sitios debe estar entre 3 y 30'
    }
    return ''
  }

  const removeManualSite = useCallback(
    async (strataName, siteIndex) => {
      const allSites = monitoringSitesBySamplingAreas[strataName]
      if (!allSites) return

      const siteToRemove = getManualSiteByDisplayIndex(allSites, siteIndex)
      if (!siteToRemove) return

      // If it's a saved site (has a real id and is NOT a newManualSite), call the API
      if (siteToRemove.properties.id && !siteToRemove.properties.newManualSite) {
        await deleteSite({ siteId: siteToRemove.properties.id })
      }

      // Remove from local state
      setMonitoringSitesBySamplingAreas(prev => {
        const newMonitoringSites = { ...prev }
        if (newMonitoringSites[strataName]) {
          newMonitoringSites[strataName] = newMonitoringSites[strataName].filter(
            site => site !== siteToRemove,
          )
        }
        return newMonitoringSites
      })
    },
    [monitoringSitesBySamplingAreas, setMonitoringSitesBySamplingAreas, deleteSite],
  )

  const addManualSite = data => {
    const { name, actualLocation } = data
    if (!actualLocation || !Array.isArray(actualLocation) || actualLocation.length !== 2) {
      return handleError(new Error('Las coordenadas son requeridas'))
    }

    const [lat, lng] = actualLocation

    // Asegurarse de que sean números
    if (
      typeof lat !== 'number' ||
      typeof lng !== 'number' ||
      Number.isNaN(lat) ||
      Number.isNaN(lng)
    ) {
      return handleError(new Error('Las coordenadas deben ser números válidos'))
    }

    // Crear punto GeoJSON con coordenadas [lng, lat] como números
    const point = turf.point([lng, lat])
    // Determinar a qué samplingArea pertenece el punto
    const foundStrata = findManualSiteStratum({
      point,
      manualSiteStrata,
      selectedStrataId: selectedStrataForManualSite?.properties?.id,
    })
    let strataName = null
    let samplingAreaId = null
    if (foundStrata) {
      strataName = foundStrata.properties.name
      samplingAreaId = foundStrata.properties.id
      // Crear el sitio con las propiedades necesarias
      point.properties.id = UUID()
      point.properties.clientId = UUID()
      point.properties.name = name || `Sitio Manual ${Date.now()}`
      point.properties.isRandomSite = false
      point.properties.strata = strataName
      point.properties.samplingAreaId = samplingAreaId
      point.properties.newManualSite = true
      // Agregar el sitio al estado
      setMonitoringSitesBySamplingAreas(prev => {
        const newMonitoringSites = { ...prev }
        newMonitoringSites[strataName] = [...(newMonitoringSites[strataName] || []), point]
        return newMonitoringSites
      })
      // Expandir el acordeón del estrato correspondiente
      setExpandedAccordion(strataName)
      setPartialChanges(true)
      return true
    }
    return handleError(null, 'No se encontró un estrato asociado al punto')
  }

  // Handlers para StrataAccordion
  const handleExpandedChange = useCallback((strataName, expanded) => {
    setExpandedAccordion(expanded ? strataName : null)
  }, [])

  const handleSitesChange = useCallback((strataName, value) => {
    const numValue = value === '' ? 0 : parseInt(value, 10)
    setStrataSites(sS => {
      return { ...sS, [strataName]: numValue }
    })
  }, [])

  const handleRandomSiteNameChange = useCallback(
    (strataName, index, newName) => {
      setMonitoringSitesBySamplingAreas(mS => {
        const newMonitoringSites = { ...mS }

        if (!mS[strataName]) {
          return newMonitoringSites
        }

        // Crear una copia del array de sitios para el estrato específico
        const updatedStrataSites = [...mS[strataName]]

        // Encontrar todos los sitios aleatorios y su posición en el array original
        let randomSiteCount = 0
        for (let i = 0; i < updatedStrataSites.length; i++) {
          if (updatedStrataSites[i].properties.isRandomSite) {
            if (randomSiteCount === index) {
              // Crear una copia del sitio y modificar su nombre
              updatedStrataSites[i] = {
                ...updatedStrataSites[i],
                properties: {
                  ...updatedStrataSites[i].properties,
                  name: newName,
                },
              }
              break
            }
            randomSiteCount++
          }
        }

        newMonitoringSites[strataName] = updatedStrataSites
        return newMonitoringSites
      })
      setPartialChanges(true)
    },
    [setPartialChanges, setMonitoringSitesBySamplingAreas],
  )

  const handleManualSiteNameChange = useCallback(
    (strataName, index, newName) => {
      setMonitoringSitesBySamplingAreas(mS => {
        if (!mS[strataName]) {
          return mS
        }

        const siteToRename = getManualSiteByDisplayIndex(mS[strataName], index)
        if (!siteToRename) {
          return mS
        }

        return {
          ...mS,
          [strataName]: mS[strataName].map(site =>
            site === siteToRename
              ? { ...site, properties: { ...site.properties, name: newName } }
              : site,
          ),
        }
      })
      setPartialChanges(true)
    },
    [setPartialChanges, setMonitoringSitesBySamplingAreas],
  )

  const handleReRandomize = useCallback(
    (strataName, index) => {
      const strata = stratas.features.find(s => s.properties.name === strataName)
      if (strata) {
        setReRandomizeIndex(index)
        setReRandomizeStrata(strata)
        setOpenReRandomizeModal(true)
      }
    },
    [stratas],
  )

  const handleDeleteManualSite = useCallback(
    (strataName, siteIndex) => {
      const allSites = monitoringSitesBySamplingAreas[strataName]
      if (!allSites) return

      const siteToRemove = getManualSiteByDisplayIndex(allSites, siteIndex)
      if (!siteToRemove) return

      // Unsaved sites: delete directly, no warning needed
      if (!siteToRemove.properties.id || siteToRemove.properties.newManualSite) {
        removeManualSite(strataName, siteIndex)
        return
      }

      // Saved sites: show confirmation modal with API check
      setConfirmationModalConfig({
        open: true,
        title: 'Eliminar sitio manual',
        message:
          '¿Está seguro de querer eliminar este sitio? Se verificará si tiene datos asociados.',
        confirmLabel: 'Eliminar',
        cancelLabel: 'Cancelar',
        confirmAction: async () => {
          setConfirmationModalConfig({ open: false })
          const siteId = siteToRemove.properties.id

          setLoadingModalConfig({
            open: true,
            title: 'Verificando datos asociados',
            dialogMessage: 'Verificando si el sitio tiene datos asociados...',
          })

          try {
            const { hasActivities, hasTasks } = await getMonitoringSiteAssociations({
              monitoringSiteId: siteId,
              farmId: farm.id,
            })

            if (hasActivities || hasTasks) {
              const associations = []
              if (hasActivities) associations.push('actividades de monitoreo')
              if (hasTasks) associations.push('tareas de monitoreo')

              setLoadingModalConfig({
                open: false,
                errorMessage: `Este sitio tiene ${associations.join(' y ')} asociados. No se puede eliminar.`,
                error: new Error('No se puede eliminar el sitio'),
              })
              return
            }
          } catch (error) {
            setLoadingModalConfig({ open: false })
            handleError(error)
            return
          }

          try {
            await removeManualSite(strataName, siteIndex)
            setLoadingModalConfig({
              open: false,
              successfulMessage: 'Sitio eliminado correctamente',
            })
          } catch {
            // The delete mutation already reported through the global channel.
            setLoadingModalConfig({ open: false })
          }
        },
      })
    },
    [
      monitoringSitesBySamplingAreas,
      removeManualSite,
      setConfirmationModalConfig,
      setLoadingModalConfig,
      farm,
      handleError,
    ],
  )

  const handleAddManualSite = useCallback(
    strata => {
      pendingStrataRef.current = strata
      setConfirmationModalConfig({
        open: true,
        title: 'Advertencia',
        message:
          'Los sitios manuales no son contemplados para muestreos SOC de Programa de carbono',
        confirmLabel: 'Continuar',
        cancelLabel: 'Cancelar',
        confirmAction: handleConfirmWarning,
      })
    },
    [setConfirmationModalConfig, handleConfirmWarning],
  )

  const handleCloseManualSiteForm = () => {
    setOpenManualSiteForm(false)
    setSelectedStrataForManualSite(null)
    setInitialCoordinates([null, null])
  }

  useEffect(() => {
    if (mapClickCoordinates) {
      setInitialCoordinates(mapClickCoordinates)
      setOpenManualSiteForm(true)
    } else {
      setInitialCoordinates([null, null])
      setOpenManualSiteForm(false)
    }
  }, [mapClickCoordinates])

  const disabledMessage = useMemo(() => {
    if (isViewOnly) {
      return ''
    }
    let message = ''
    const enabledSites = Object.entries(strataSites).filter(
      ([name]) => !lockedStrataNames.has(name),
    )
    if (enabledSites.length === 0) {
      const lockedStrataInPanel = Object.keys(strataSites).filter(name =>
        lockedStrataNames.has(name),
      )
      const noneHaveRandomSites = lockedStrataInPanel.every(
        name => !strataWithRandomSites.has(name),
      )
      message =
        lockedStrataInPanel.length > 0 && noneHaveRandomSites
          ? 'Ningún estrato tiene área efectiva disponible para aleatorizar.'
          : "Todos los estratos ya tienen sitios aleatorios. Usa la opción 'Re-aleatorizar' por sitio si necesitas cambios."
    } else if (!enabledSites.every(([, sites]) => sites > 0)) {
      message =
        'Debes elegir el número de sitios de muestreo para los estratos habilitados antes de aleatorizar'
    }
    if (effectiveAreasData?.canRandomize === false) {
      message = effectiveAreasData?.blockingMessages?.join('. ') || 'No se puede aleatorizar'
    }
    return message
  }, [strataSites, lockedStrataNames, strataWithRandomSites, effectiveAreasData, isViewOnly])

  const totalEffectiveArea = useMemo(
    () => effectiveAreasData?.effectiveAreas?.reduce((sum, ea) => sum + ea.effectiveArea, 0) || 0,
    [effectiveAreasData],
  )

  return (
    <>
      <ReRandomizeModal
        handleCloseReRandomizeModal={handleCloseReRandomizeModal}
        handleConfirmReRandomizeModal={handleConfirmReRandomizeModal}
        index={reRandomizeIndex}
        open={openReRandomizeModal}
        strata={reRandomizeStrata}
      />
      <AddManualSiteFormModal
        initialCoordinates={initialCoordinates}
        monitoringSitesBySamplingAreas={monitoringSitesBySamplingAreas}
        open={openManualSiteForm}
        selectedStrata={selectedStrataForManualSite}
        stratas={manualSiteStrata}
        onClose={handleCloseManualSiteForm}
        onSubmit={addManualSite}
      />
      {partialChanges && (
        <Box sx={{ px: 1, py: 2 }}>
          <CustomAlert
            action={null}
            message="Tienes sitios sin guardar. Si navegas fuera de esta página, perderás los sitios que aún no has guardado. Guarda el plan de monitoreo antes de salir."
            severity="warning"
            title="Sitios sin guardar"
          />
        </Box>
      )}
      <Stack sx={{ height: '65vh', overflowY: 'scroll' }}>
        <Box>
          {stratas &&
            stratas.features.map(strata => {
              const strataName = strata.properties.name
              const isLocked = lockedStrataNames.has(strataName)
              const lockReason = isLocked
                ? disabledStrataNames.has(strataName)
                  ? 'zero-area'
                  : 'has-random-sites'
                : undefined
              return (
                <StrataAccordion
                  key={strata.properties.id || strataName}
                  canEditSites={!isLocked}
                  checkValue={checkValue}
                  disabledTooltip=""
                  effectiveAreas={effectiveAreasData?.effectiveAreas}
                  expanded={expandedAccordion === strataName}
                  hasUnsavedSites={hasUnsavedSites(strataName)}
                  isViewOnly={isViewOnly}
                  lockReason={lockReason}
                  monitoringSitesBySamplingAreas={monitoringSitesBySamplingAreas}
                  reRandomize={reRandomize}
                  strata={strata}
                  totalEffectiveArea={totalEffectiveArea}
                  wasSaved={wasSaved}
                  onAddManualSite={handleAddManualSite}
                  onDeleteManualSite={handleDeleteManualSite}
                  onExpandedChange={handleExpandedChange}
                  onManualSiteNameChange={handleManualSiteNameChange}
                  onRandomSiteNameChange={handleRandomSiteNameChange}
                  onReRandomize={handleReRandomize}
                  onSitesChange={handleSitesChange}
                />
              )
            })}
        </Box>
      </Stack>
      {stratas && (
        <Stack direction="row" justifyContent="center" spacing={2} sx={{ pt: 2, pb: 2 }}>
          <Tooltip placement="top" title={disabledMessage}>
            <span>
              <LoadingButton
                disabled={
                  effectiveAreasData?.canRandomize === false ||
                  isViewOnly ||
                  Object.keys(strataSites).filter(name => !lockedStrataNames.has(name)).length ===
                    0 ||
                  Object.keys(strataSites)
                    .filter(name => !lockedStrataNames.has(name))
                    .some(siteName => checkValue(siteName)) ||
                  !Object.entries(strataSites)
                    .filter(([name]) => !lockedStrataNames.has(name))
                    .every(([, sites]) => sites > 0)
                }
                endIcon={<AddLocationIcon />}
                loading={loading}
                loadingPosition="end"
                size="large"
                sx={{ minWidth: 200 }}
                variant="contained"
                onClick={randomizeMonitoringSites}
              >
                Aleatorizar Sitios
              </LoadingButton>
            </span>
          </Tooltip>
          {partialChanges && (
            <LoadingButton
              color="primary"
              disabled={isViewOnly}
              endIcon={<CheckCircleIcon />}
              loading={loading}
              loadingPosition="end"
              size="large"
              sx={{ minWidth: 200 }}
              variant="contained"
              onClick={saveSamplePlanning}
            >
              Guardar Plan de Monitoreo
            </LoadingButton>
          )}
        </Stack>
      )}
    </>
  )
}

export default SamplePlanningPanel
